// src/components/AuthPages.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import SignInButton from './auth/SignInButton';
import logger from '../utils/logger';

const AuthPages = ({ onLogin }) => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Redirect to portal only when MSAL actually has an account.
  // Do NOT check localStorage here — stale localStorage from other tabs would
  // cause an immediate redirect before authentication completes.
  useEffect(() => {
    if ((isAuthenticated || accounts.length > 0) && window.location.pathname === '/login') {
      logger.log("AuthPages - MSAL account present, redirecting to portal");
      if (onLogin) onLogin();
      navigate('/portal');
    }
  }, [isAuthenticated, accounts, navigate, onLogin]);

  // Only show redirect spinner when MSAL genuinely has an account — not based on
  // localStorage alone, which may be stale from another tab.
  if ((isAuthenticated || accounts.length > 0) && window.location.pathname === '/login') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="mt-3 text-indigo-600">Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      {/* Header */}
      <header className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-indigo-600 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">SC</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">SiriusConnect365</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col justify-center w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900">Sign in to SiriusConnect365</h2>
            <p className="mt-2 text-sm text-gray-600">
              Access your payslips, profile, and employee documents
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow rounded-lg sm:px-10">
            <SignInButton />
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact{' '}
              <a href="mailto:support@dogmagroup.co.uk" className="font-medium text-indigo-600 hover:text-indigo-500">
                support@dogmagroup.co.uk
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-center text-gray-400">
            &copy; {new Date().getFullYear()} SiriusConnect365. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AuthPages;
